import type { CSSProperties } from 'react';
import { palette } from '../tokens.ts';

export interface RhumbLinesProps {
  /** Origin of the lines as a fraction of the box (0..1). */
  originX?: number;
  originY?: number;
  /** Number of lines; 32 matches the points of the rose. */
  count?: number;
  color?: string;
  opacity?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Thin rhumb lines radiating from a compass rose across the whole chart.
 * Every fourth line (the eight principal winds) is drawn a little heavier.
 */
export function RhumbLines({
  originX = 0.5,
  originY = 0.5,
  count = 32,
  color = palette.ink,
  opacity = 0.16,
  className,
  style,
}: RhumbLinesProps) {
  const cx = originX * 1000;
  const cy = originY * 1000;
  const reach = 3000;
  return (
    <svg
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        ...style,
      }}
      viewBox="0 0 1000 1000"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <g stroke={color} opacity={opacity} fill="none">
        {Array.from({ length: count }, (_, i) => {
          const angle = (i / count) * Math.PI * 2;
          const principal = i % (count / 8) === 0;
          return (
            <line
              key={i}
              x1={cx}
              y1={cy}
              x2={cx + Math.cos(angle) * reach}
              y2={cy + Math.sin(angle) * reach}
              strokeWidth={principal ? 1.1 : 0.5}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </g>
    </svg>
  );
}
